import { useSwitch } from '@mui/base';
import cn from 'classnames';
import { forwardRef, useCallback, useContext } from 'react';

import RadioCircleIcon from '../../../../assets/icons/radio-circle.svg?react';
import { useHover } from '../../../utils/hooks';
import { RadioGroupContext } from '../radio-group/radio-group-context';

import type { ForwardedRef, KeyboardEvent, MouseEvent, ReactElement } from 'react';

import type { RadioProps } from './radio.types';

import './radio.scss';

/**
 * Radio buttons are used to pick an option when only one option can be chosen.
 * A single radio button can be used representing a singular option,
 * for the purpose of selecting an element between a group of options.
 *
 * ### Usage
 *
 * ```tsx
 * import { useState } from 'react';
 * import { Radio } from '@bloomreach/react-banana-ui';
 *
 * export default function MyRadioComponent() {
 *   const [checked, setChecked] = useState(false);
 *   return (
 *     <Radio
 *       checked={checked}
 *       onChange={(event) => setChecked(event.target.checked)}
 *    />
 *   );
 * }
 * ```
 */

const Radio = forwardRef((props: RadioProps, forwardedRef: ForwardedRef<HTMLInputElement> | null): ReactElement => {
  const {
    checked: checkedProp,
    className,
    defaultChecked,
    disabled: disabledProp = false,
    inputProps,
    name,
    onChange: onChangeProp,
    readOnly: readOnlyProp = false,
    required: requiredProp = false,
    value,
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const radioGroupContext = useContext(RadioGroupContext);

  const radioChecked =
    checkedProp === undefined && value !== undefined && value !== null
      ? radioGroupContext?.value === value
      : checkedProp;

  const useRadioProps = {
    checked: radioChecked,
    defaultChecked,
    disabled: disabledProp || radioGroupContext?.disabled,
    onChange: onChangeProp || radioGroupContext?.onChange,
    readOnly: readOnlyProp || radioGroupContext?.readOnly,
    required: requiredProp || radioGroupContext?.required,
  };

  const { checked, disabled, focusVisible, getInputProps, readOnly } = useSwitch(useRadioProps);

  const { onChange, ...inputPropsInternal } = getInputProps({
    name: name || radioGroupContext?.name,
    role: undefined,
    type: 'radio',
    ...inputProps,
    className: cn('rbui-radio__input', inputProps?.className),
  });

  const handleClick = useCallback(
    (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>): void => {
      if (readOnly) {
        event.preventDefault();
      }
    },
    [readOnly],
  );

  const { handlers, hovered } = useHover();

  const classes = cn('rbui-radio', className, {
    'rbui-radio--checked': checked,
    'rbui-radio--disabled': disabled,
    'rbui-radio--focus-visible': focusVisible && !disabled,
    'rbui-radio--hovered': hovered,
    'rbui-radio--readonly': readOnly,
  });

  return (
    <span
      aria-checked={checked}
      aria-disabled={disabled}
      aria-readonly={readOnly}
      className={classes}
      {...rest}
      {...handlers}
    >
      <div aria-hidden className="rbui-radio__inner">
        {checked && <RadioCircleIcon />}
      </div>
      <input {...inputPropsInternal} onChange={onChange} onClick={handleClick} ref={forwardedRef} value={value} />
    </span>
  );
});

Radio.displayName = 'Radio';
export default Radio;
